import React, { useState } from "react";
import { Link } from "react-router-dom";
import { X, Minus, Plus, ArrowRight } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useLang } from "@/contexts/LanguageContext";
import { formatPrice, createCheckoutSession } from "@/lib/api";
import { toast } from "sonner";

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal } = useCart();
  const { lang, t } = useLang();
  const [processing, setProcessing] = useState(false);

  const handleCheckout = async () => {
    if (items.length === 0) return;
    setProcessing(true);
    try {
      const payload = {
        items: items.map((it) => ({
          product_id: it.product_id,
          quantity: it.quantity,
          size: it.size,
          color: it.color,
        })),
        origin_url: window.location.origin,
        locale: lang,
      };
      const res = await createCheckoutSession(payload);
      window.location.href = res.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setProcessing(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        data-testid="cart-overlay"
        onClick={() => setIsOpen(false)}
        className={`fixed inset-0 bg-maly-navy/40 z-[60] transition-opacity duration-500 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />
      {/* Drawer */}
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 h-full w-full sm:w-[480px] bg-maly-cream z-[70] flex flex-col transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between p-6 lg:p-8 border-b border-maly-navy/10">
          <div>
            <div className="font-body text-[10px] tracking-[0.3em] uppercase opacity-60">{t.cart.overline}</div>
            <h2 className="font-display text-2xl mt-1">{t.cart.headline}</h2>
          </div>
          <button
            data-testid="close-cart"
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-maly-navy/5"
            aria-label="Close cart"
          >
            <X size={20} strokeWidth={1.4} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 lg:px-8 py-6">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center gap-6">
              <div className="font-display text-3xl">{t.common.emptyCart}</div>
              <p className="font-body text-sm opacity-60 max-w-xs">{t.common.emptyCartCta}</p>
              <Link
                to="/shop"
                onClick={() => setIsOpen(false)}
                data-testid="empty-cart-shop"
                className="btn-primary"
              >
                {t.nav.shop}
              </Link>
            </div>
          ) : (
            <ul className="space-y-6">
              {items.map((it) => (
                <li key={it.key} data-testid={`cart-item-${it.product_id}`} className="flex gap-4 pb-6 border-b border-maly-navy/10">
                  <Link to={`/shop/${it.slug}`} onClick={() => setIsOpen(false)} className="w-24 h-32 bg-maly-creamDeep flex-shrink-0 img-zoom">
                    <img src={it.image} alt="" className="w-full h-full object-cover" />
                  </Link>
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <Link to={`/shop/${it.slug}`} onClick={() => setIsOpen(false)} className="font-display text-lg leading-tight">
                        {it.name?.[lang] || it.name?.en || it.slug}
                      </Link>
                      <div className="font-body text-xs opacity-60 mt-1 tracking-wider">
                        {[it.size, it.color].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-maly-navy/20">
                        <button
                          data-testid={`cart-qty-down-${it.product_id}`}
                          onClick={() => updateQty(it.key, it.quantity - 1)}
                          className="p-2"
                          aria-label="Decrease"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="px-2 font-body text-sm">{it.quantity}</span>
                        <button
                          data-testid={`cart-qty-up-${it.product_id}`}
                          onClick={() => updateQty(it.key, it.quantity + 1)}
                          className="p-2"
                          aria-label="Increase"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <div className="font-body text-sm">{formatPrice(it.price * it.quantity, it.currency)}</div>
                    </div>
                    <button
                      data-testid={`cart-remove-${it.product_id}`}
                      onClick={() => removeItem(it.key)}
                      className="font-body text-[10px] tracking-[0.2em] uppercase opacity-50 hover:opacity-100 self-start mt-2"
                    >
                      {t.common.remove}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-maly-navy/10 p-6 lg:p-8 space-y-4 bg-maly-cream">
            <div className="flex items-center justify-between font-body text-sm">
              <span className="tracking-[0.2em] uppercase text-xs opacity-60">{t.common.subtotal}</span>
              <span data-testid="cart-subtotal" className="font-display text-2xl">{formatPrice(subtotal)}</span>
            </div>
            <p className="font-body text-xs opacity-50">{t.common.shippingNote}</p>
            <button
              data-testid="checkout-button"
              onClick={handleCheckout}
              disabled={processing}
              className="btn-primary w-full disabled:opacity-60"
            >
              {processing ? t.common.processing : t.common.proceedToCheckout}
              <ArrowRight size={14} />
            </button>
            <button
              data-testid="continue-shopping"
              onClick={() => setIsOpen(false)}
              className="font-body text-[11px] tracking-[0.25em] uppercase opacity-70 hover:opacity-100 w-full text-center pt-2"
            >
              {t.common.continueShopping}
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
